import React from 'react';
import Icon from '../../../components/AppIcon';
import type { Milestone } from '../types';

const TimelineSection: React.FC = () => {
  const milestones: Milestone[] = [
    {
      id: '1',
      year: '2019',
      title: 'The Inception',
      description: 'Make Mind Automations was founded in a garage with a single laptop and a vision to kill the "Ctrl+C, Ctrl+V" culture.'
    },
    {
      id: '2',
      year: '2020',
      title: 'First Clients',
      description: 'Despite the global pandemic, we helped businesses pivot to digital-first operations, saving many hours.'
    },
    {
      id: '3',
      year: '2021',
      title: 'Official Partnerships',
      description: 'Recognized as certified experts by n8n, Make.com, and Meta (WhatsApp API), validating our technical rigor.'
    },
    {
      id: '4',
      year: '2022',
      title: 'The AI Revolution',
      description: 'We integrated GPT-3 before it was cool. Pioneered our first "Intelligent Chatbot" for real estate lead qualification.'
    },
    {
      id: '5',
      year: '2023',
      title: 'Scaling Up',
      description: 'Grew our client base. Our team grew with automation engineers and solution architects.'
    },
    {
      id: '6',
      year: '2024-25',
      title: 'Industry Leaders',
      description: 'Launching our proprietary "AI Agent Framework" to handle complex, multi-step business logic autonomously.'
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-surface overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1 rounded-full">
            <Icon name="Activity" size={16} />
            <span className="text-xs font-bold uppercase tracking-wide">Our History</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-text-primary">
            From Startup to <span className="text-primary">Standard Setter</span>
          </h2>
        </div>

        <div className="relative">
          {/* Central Vertical Line (Desktop) */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-border hidden lg:block">
            <div className="absolute top-0 left-0 w-full h-1/3 bg-gradient-to-b from-primary to-transparent opacity-50" />
          </div>

          <div className="space-y-12">
            {milestones.map((milestone, index) => (
              <div
                key={milestone.id}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } flex-col lg:gap-12`}
              >
                {/* Content Side */}
                <div className={`w-full lg:w-[calc(50%-24px)] ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                  <div className="relative group bg-card p-8 rounded-2xl border border-border shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    
                    {/* Year Badge */}
                    <div className={`absolute top-0 ${index % 2 === 0 ? 'right-0 rounded-bl-2xl' : 'left-0 rounded-br-2xl'} bg-primary px-4 py-2 text-white font-bold text-lg rounded-tl-none rounded-tr-none shadow-md`}>
                      {milestone.year}
                    </div>

                    <div className="mt-6">
                      <h3 className="text-xl font-bold text-text-primary mb-3 group-hover:text-primary transition-colors">
                        {milestone.title}
                      </h3>
                      <p className="text-text-secondary leading-relaxed">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Center Dot */}
                <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center w-12 h-12 hidden lg:flex">
                  <div className="w-4 h-4 bg-primary rounded-full ring-4 ring-background shadow-lg z-10 relative">
                     <div className="absolute -inset-2 bg-primary/30 rounded-full animate-ping opacity-75"></div>
                  </div>
                </div>

                {/* Empty Spacer Side */}
                <div className="hidden lg:block w-[calc(50%-24px)]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;